import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
} from '@mui/material';
import {
  CloudUpload,
  Assessment,
  Storage,
  Settings,
  Dashboard,
  People,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const menuItems = [
  {
    text: 'Upload & Process',
    icon: <CloudUpload />,
    path: '/upload',
  },
  {
    text: 'Compliance Results',
    icon: <Assessment />,
    path: '/results',
  },
  {
    text: 'Database Monitor',
    icon: <Storage />,
    path: '/database',
    adminOnly: true,
  },
  {
    text: 'Monitoring Dashboard',
    icon: <Dashboard />,
    path: '/monitoring',
    adminOnly: true,
  },
  {
    text: 'User Management',
    icon: <People />,
    path: '/users',
    adminOnly: true,
  },
  {
    text: 'Settings',
    icon: <Settings />,
    path: '/settings',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (window.innerWidth < 960) {
      onToggle(); // Close sidebar on mobile after navigation
    }
  };

  // Filter menu items based on user role
  const visibleMenuItems = menuItems.filter((item: any) => {
    if (item.adminOnly && user?.role !== 'admin') {
      return false; // Hide admin-only items for non-admin users
    }
    return true;
  });

  const drawerContent = (
    <Box sx={{ mt: 8 }}>
      <List>
        {visibleMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={!open ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    bgcolor: isActive ? 'primary.light' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.light' : 'action.hover',
                    },
                    borderRadius: 1,
                    mx: 1,
                    my: 0.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: isActive ? 'primary.main' : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: open ? 1 : 0,
                      color: isActive ? 'primary.main' : 'inherit',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? 240 : 60,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 60,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          bgcolor: 'grey.50',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;